import React, { forwardRef } from 'react';
import GlyphAtom from '../Glyph/GlyphAtom';
import './TagAtom.scss';

/**
 * TagAtom — generic tinted label primitive.
 *
 * Replaces the marketplace-specific ItemTypeChipAtom / SchemaChipAtom /
 * ScopeChipAtom / TypeFacetChipAtom / ScopePillAtom / WIPillAtom atoms,
 * which all rendered the same `<span><i/><label/></span>` shape with
 * domain-coupled tinting. Callers map their domain enums (scope, item
 * type, etc.) to a `tone` here.
 *
 * Sibling primitive to `StatusBadgeAtom` (uses the same tone palette);
 * the difference is that `TagAtom` may be interactive (renders a
 * `<button>` when `onClick` is provided) and supports a count badge for
 * the facet-filter pattern.
 */
export type TagTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warn'
  | 'danger'
  | 'accent'
  | 'teal'
  | 'outline';

export type TagShape = 'chip' | 'pill';

export type TagSize = 'small' | 'medium' | 'large';

export interface TagAtomProps {
  /** Visible text content. */
  label: string;
  /** Optional PrimeIcon class without the `pi ` prefix (e.g. `pi-compass`). */
  icon?: string;
  /**
   * Optional PrimeIcon rendered after the label (e.g. `pi-arrow-right`) — a check chip's
   * "this routes somewhere" affordance, distinct from `icon`'s leading status marker.
   */
  trailingIcon?: string;
  /** Color/tone token. Default `neutral`. */
  tone?: TagTone;
  /** `pill` = fully rounded (default), `chip` = small rounded corners. */
  shape?: TagShape;
  /** Default `medium`. `small` is denser (used inside cards). */
  size?: TagSize;
  /**
   * Allows the label to wrap onto multiple lines instead of the default single-line treatment —
   * for a sentence-length caveat inside a narrow column (e.g. a lane-label pending notice).
   * Default `false` (`nowrap`) so no existing consumer changes.
   */
  wrap?: boolean;
  /** Numeric badge rendered on the right (used by facet filters). */
  count?: number;
  /**
   * Selected/pressed state. Renders `aria-pressed` when interactive and
   * applies a solid-fill visual treatment that overrides `tone`.
   */
  pressed?: boolean;
  /** Strikethrough/dimmed treatment for "excluded" or "disabled" tags. */
  excluded?: boolean;
  /** When provided, the tag renders as a `<button>` and forwards clicks. */
  onClick?: () => void;
  /** Optional accessible label override (defaults to `label`). */
  ariaLabel?: string;
  /**
   * Supplementary explanation shown on hover — the provenance detail behind a marker
   * (why a `global` perspective cannot be refused, what an override overlays). Never the
   * tag's only statement of a reason a user must act on; that belongs in visible text.
   */
  title?: string;
  className?: string;
  'data-testid'?: string;
  /** Parity instrumentation — forwarded to the rendered root element. */
  'data-datum'?: string;
  'data-kind'?: string;
}

type TagRef = HTMLButtonElement | HTMLSpanElement;

const TagAtom = forwardRef<TagRef, TagAtomProps>((props, ref) => {
  const {
    label,
    icon,
    trailingIcon,
    tone = 'neutral',
    shape = 'pill',
    size = 'medium',
    wrap = false,
    count,
    pressed = false,
    excluded = false,
    onClick,
    ariaLabel,
    title,
    className,
    'data-testid': testId,
    'data-datum': datum,
    'data-kind': datumKind,
  } = props;

  const classes = [
    'tag-atom',
    `tag-atom--shape-${shape}`,
    `tag-atom--size-${size}`,
    `tag-atom--tone-${tone}`,
    pressed ? 'tag-atom--pressed' : '',
    excluded ? 'tag-atom--excluded' : '',
    wrap ? 'tag-atom--wrap' : '',
    onClick ? 'tag-atom--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {icon && (
        icon.includes(':')
          ? <GlyphAtom glyph={icon} className="tag-atom__icon" aria-label="" />
          : <i className={`pi ${icon} tag-atom__icon`} aria-hidden="true" />
      )}
      <span className="tag-atom__label">{label}</span>
      {typeof count === 'number' && (
        <span className="tag-atom__count">{count}</span>
      )}
      {trailingIcon && <i className={`pi ${trailingIcon} tag-atom__trailing-icon`} aria-hidden="true" />}
    </>
  );

  if (onClick) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={classes}
        onClick={onClick}
        aria-pressed={pressed}
        aria-label={ariaLabel}
        title={title}
        data-testid={testId}
        data-datum={datum}
        data-kind={datumKind}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      className={classes}
      role="status"
      aria-label={ariaLabel}
      title={title}
      data-testid={testId}
      data-datum={datum}
      data-kind={datumKind}
    >
      {content}
    </span>
  );
});

TagAtom.displayName = 'TagAtom';

export default TagAtom;
